import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X, ArrowRight, Search, Bug } from 'lucide-react';
import {
  loadTransferMembers,
  isTransferDebugMode,
  setTransferDebugMode,
  type TransferMember,
  type TransferMembersLoadResult,
} from '../services/taskTransferMembers';

interface TaskTransferModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (userId: string, userName: string, justification: string) => void;
  currentUserId: string;
  organizationId: string;
  isLoading?: boolean;
}

export const TaskTransferModal: React.FC<TaskTransferModalProps> = ({
  open,
  onClose,
  onConfirm,
  currentUserId,
  organizationId,
  isLoading,
}) => {
  const [allMembers, setAllMembers] = useState<TransferMember[]>([]);
  const [baseDiagnostics, setBaseDiagnostics] = useState<TransferMembersLoadResult['diagnostics'] | null>(null);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<TransferMember | null>(null);
  const [justification, setJustification] = useState('');
  const [loading, setLoading] = useState(false);
  const [debugMode, setDebugMode] = useState(isTransferDebugMode);
  const [loadError, setLoadError] = useState<string | null>(null);

  const reloadMembers = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const result = await loadTransferMembers({
        organizationId,
        currentUserId,
        debugMode,
      });
      setAllMembers(result.members);
      setBaseDiagnostics(result.diagnostics);
      if (result.diagnostics.error) {
        setLoadError(result.diagnostics.error);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Falha ao carregar membros';
      setLoadError(msg);
      setAllMembers([]);
      console.error('[TaskTransfer] Falha inesperada:', err);
    } finally {
      setLoading(false);
    }
  }, [organizationId, currentUserId, debugMode]);

  useEffect(() => {
    if (!open || !organizationId) return;
    reloadMembers();
  }, [open, organizationId, reloadMembers]);

  const members = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allMembers;
    return allMembers.filter(
      (m) => m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q),
    );
  }, [allMembers, search]);

  const diagnostics = useMemo(() => {
    if (!baseDiagnostics) return null;
    const q = search.trim();
    if (!q) return baseDiagnostics;
    const searchFilter = `Busca: "${q}"`;
    return {
      ...baseDiagnostics,
      afterSearch: members.length,
      appliedFilters: [
        ...baseDiagnostics.appliedFilters.filter((f) => !f.startsWith('Busca:')),
        searchFilter,
      ],
    };
  }, [baseDiagnostics, search, members.length]);

  useEffect(() => {
    if (!open) {
      setSelectedUser(null);
      setJustification('');
      setSearch('');
      setBaseDiagnostics(null);
      setLoadError(null);
    }
  }, [open]);

  const toggleDebugMode = useCallback(() => {
    const next = !debugMode;
    setDebugMode(next);
    setTransferDebugMode(next);
  }, [debugMode]);

  const handleConfirm = useCallback(() => {
    if (!selectedUser || !justification.trim()) return;
    onConfirm(selectedUser.id, selectedUser.name, justification.trim());
  }, [selectedUser, justification, onConfirm]);

  const emptyReason = (() => {
    if (loadError) return `Erro ao consultar membros: ${loadError}`;
    if (!diagnostics) return 'Carregando dados da organização…';
    if (diagnostics.rawMembershipCount === 0) {
      return 'Nenhum vínculo em user_organizations para esta organização.';
    }
    if (diagnostics.afterExcludeSelf === 0) {
      return 'Apenas o usuário atual está vinculado à organização (filtro: excluir usuário atual).';
    }
    if (diagnostics.profileRowsFound === 0) {
      return 'Vínculos encontrados, mas nenhum perfil em user_profiles — usuários sem cadastro de perfil não aparecem na lista.';
    }
    if (diagnostics.afterProfileHydration === 0) {
      return 'Perfis não correspondem aos vínculos da organização.';
    }
    if (diagnostics.afterSearch === 0 && search.trim()) {
      return `Nenhum membro corresponde à busca "${search.trim()}".`;
    }
    return 'Nenhum membro elegível encontrado.';
  })();

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="w-full max-w-md rounded border border-[var(--border-default)] bg-[var(--bg-surface)] shadow-xl dark:bg-[var(--bg-elevated)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[var(--border-default)] px-5 py-3">
          <h3 className="font-mono text-[11px] font-bold uppercase tracking-[0.1em] enterprise-text-primary">
            Transferir tarefa
          </h3>
          <div className="flex items-center gap-2">
            {import.meta.env.DEV && (
              <button
                type="button"
                onClick={toggleDebugMode}
                title="Modo depuração — lista todos os vínculos sem excluir usuário atual"
                className={`rounded p-1 ${debugMode ? 'bg-amber-500/20 text-amber-600' : 'enterprise-text-muted hover:enterprise-text-primary'}`}
              >
                <Bug size={13} />
              </button>
            )}
            <button type="button" onClick={onClose} className="enterprise-text-muted hover:enterprise-text-primary">
              <X size={14} />
            </button>
          </div>
        </div>

        <div className="space-y-3 px-5 py-4">
          {import.meta.env.DEV && debugMode && (
            <div className="rounded border border-amber-500/40 bg-amber-500/10 px-2.5 py-1.5 text-[9px] text-amber-800 dark:text-amber-300">
              <strong>Modo depuração ativo</strong> — exibe todos os vínculos de user_organizations (inclui usuário atual). Console: pipeline completo.
            </div>
          )}

          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-2.5 enterprise-text-muted" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar membro..."
              className="w-full border border-[var(--border-default)] bg-[var(--bg-app)] py-2 pl-8 pr-3 text-[11px] enterprise-text-primary focus:border-blue-500 focus:outline-none"
              autoFocus
            />
          </div>

          <div className="max-h-[200px] overflow-y-auto rounded border border-[var(--border-default)]">
            {loading ? (
              <p className="px-3 py-4 text-center text-[10px] enterprise-text-muted">Carregando membros…</p>
            ) : members.length === 0 ? (
              <div className="space-y-2 px-3 py-4">
                <p className="text-center text-[10px] font-medium text-amber-700 dark:text-amber-400">
                  {emptyReason}
                </p>
                {diagnostics && (
                  <div className="rounded border border-[var(--border-default)] bg-[var(--bg-app)] px-2.5 py-2 text-[9px] enterprise-text-muted">
                    <p className="font-semibold uppercase tracking-wide enterprise-text-secondary">Diagnóstico</p>
                    <ul className="mt-1 space-y-0.5">
                      <li>Fonte: <code className="font-mono">{diagnostics.source}</code></li>
                      <li>user_organizations: {diagnostics.rawMembershipCount}</li>
                      <li>Após excluir atual: {diagnostics.afterExcludeSelf}</li>
                      <li>user_profiles: {diagnostics.profileRowsFound}</li>
                      <li>Após hidratar: {diagnostics.afterProfileHydration}</li>
                      <li>Após busca: {diagnostics.afterSearch}</li>
                    </ul>
                    {diagnostics.appliedFilters.length > 0 && (
                      <p className="mt-1.5">
                        Filtros: {diagnostics.appliedFilters.join(' · ')}
                      </p>
                    )}
                    <p className="mt-1.5 text-[8px] opacity-80">
                      Sem filtros de departamento, cargo, hierarquia, unidade, workflow ou permissões.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              members.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setSelectedUser(m)}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left text-[11px] transition ${
                    selectedUser?.id === m.id
                      ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-400'
                      : 'enterprise-text-primary hover:bg-[var(--bg-elevated)]'
                  }`}
                >
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[9px] font-bold dark:bg-slate-700">
                    {m.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[11px] font-medium">
                      {m.name}
                      {debugMode && m.id === currentUserId && (
                        <span className="ml-1 text-[8px] font-bold text-amber-600">(você)</span>
                      )}
                    </p>
                    <p className="truncate text-[9px] enterprise-text-muted">{m.email || m.id}</p>
                  </div>
                </button>
              ))
            )}
          </div>

          <textarea
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            placeholder="Motivo da transferência (obrigatório)..."
            rows={2}
            className="w-full resize-none border border-[var(--border-default)] bg-[var(--bg-app)] px-3 py-2 text-[11px] enterprise-text-primary focus:border-blue-500 focus:outline-none"
          />
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-[var(--border-default)] px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-[var(--border-default)] px-4 py-1.5 text-[10px] font-medium enterprise-text-secondary hover:bg-[var(--bg-elevated)]"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!selectedUser || !justification.trim() || isLoading}
            className="flex items-center gap-1.5 rounded bg-blue-600 px-4 py-1.5 text-[10px] font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ArrowRight size={11} />
            {isLoading ? 'Transferindo...' : 'Transferir'}
          </button>
        </div>
      </div>
    </div>
  );
};
